import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ClaimChartApi, ClaimChartRequest, ClaimChartResponse } from '../claim-chart-api';
import { GuestAwareHttpService } from '../../services/guest-aware-http-service';
import { formatTimeoutError } from '../../../utils/timeout-handler';
import { resolveApiPath } from '@/utils/api-path-resolver';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import { injectService } from '@patsnap/tahiti-core';
import { TrackService } from '@/domains/shared/services/track-service';
import { buildAiccEventDetail } from '@/presentation/utils/aicc-event-tracking';
import { normalizeRedRevisionMappingGroup } from '@/domains/essentiality-claim/red-revision-mappings';
import { normalizeRedAnalysisMappingGroup } from '@/domains/essentiality-claim/red-analysis-mappings';
import {
    flattenMultiClaimResultGroups,
    normalizeMultiClaimResultGroups,
} from '@/domains/essentiality-claim/multi-claim-results';

@injectable()
export class ClaimChartApiImpl extends GuestAwareHttpService implements ClaimChartApi {
    private getCompareStage(request: ClaimChartRequest) {
        if (request.stage === 'judge') {
            return 'judge';
        }
        return request.stage === 'red' ? 'red' : 'blue';
    }

    private getFirstMapping(allMappings: any[] = []) {
        return allMappings.find(mappingGroup => Array.isArray(mappingGroup?.mappings) && mappingGroup.mappings.length > 0)
            ?.mappings?.[0] || {};
    }

    private normalizeJudgeStance(stance: any) {
        if (!stance) {
            return null;
        }

        if (typeof stance === 'string') {
            return { side: stance };
        }

        return stance;
    }

    private toStringArray(value: any): string[] {
        if (Array.isArray(value)) {
            return value.filter(item => item != null).map(item => String(item));
        }
        if (value == null || value === '') {
            return [];
        }
        return [String(value)];
    }

    private normalizeRedRevisionAllMappings(content: any) {
        const blueAllMappings = Array.isArray(content?.blue_result?.all_mappings)
            ? content.blue_result.all_mappings
            : [];
        const redAllMappings = Array.isArray(content?.red_result?.all_mappings)
            ? content.red_result.all_mappings
            : [];

        if (redAllMappings.length > 0) {
            const revisionAllMappings = redAllMappings
                .map((mappingGroup: any) => normalizeRedRevisionMappingGroup(mappingGroup, blueAllMappings))
                .filter(Boolean);
            console.debug('[ClaimChartApi] Normalized red revision mappings:', {
                redMappingGroupsCount: redAllMappings.length,
                deletedMappingsCount: revisionAllMappings.reduce((count: number, mappingGroup: any) => (
                    count + (mappingGroup.mappings || []).filter((mapping: any) => mapping.isDeleted).length
                ), 0),
                revisionMappingsCount: revisionAllMappings.reduce((count: number, mappingGroup: any) => (
                    count + (mappingGroup.mappings || []).filter((mapping: any) => !mapping.isDeleted && !mapping.isEmpty).length
                ), 0),
            });
            return revisionAllMappings;
        }

        const mappingChallenges = Array.isArray(content?.red_result?.mapping_challenges)
            ? content.red_result.mapping_challenges
            : [];

        return mappingChallenges
            .filter((challenge: any) => challenge?.claim_feature)
            .map((challenge: any) => {
                const evidenceContexts = Array.isArray(challenge.suggested_evidence_contexts)
                    ? challenge.suggested_evidence_contexts
                    : [];
                const suggestedSections = this.toStringArray(challenge.suggested_ts_section);
                const suggestedContents = this.toStringArray(challenge.suggested_ts_content);

                const normalizedContexts = evidenceContexts.length > 0
                    ? evidenceContexts
                    : suggestedContents.map((content, index) => ({
                        context_text: content,
                        highlight_start: 0,
                        highlight_end: content.length,
                        section_number: suggestedSections[index] || '',
                    }));

                const firstContext = normalizedContexts[0] || {};

                return {
                    feature_index: challenge.feature_index,
                    claim_feature: challenge.claim_feature,
                    review_action: challenge.review_action,
                    review_challenge: challenge.challenge,
                    review_evidence_strength: challenge.evidence_strength,
                    mappings: [{
                        ts_code: firstContext.ts_code || '',
                        version: firstContext.version || '',
                        ts_section: normalizedContexts.map((context: any, index: number) => (
                            context.section_number || suggestedSections[index] || ''
                        )),
                        ts_content: normalizedContexts.map((context: any, index: number) => (
                            context.evidence_text || context.context_text || suggestedContents[index] || ''
                        )),
                        evidence_contexts: normalizedContexts,
                        reason: challenge.challenge || '',
                        entity_mapping: challenge.entity_mapping_corrections || {},
                        review_action: challenge.review_action,
                        review_challenge: challenge.challenge,
                        review_evidence_strength: challenge.evidence_strength,
                    }],
                };
            });
    }

    private normalizeRedAllMappings(content: any) {
        const blueAllMappings = Array.isArray(content?.blue_result?.all_mappings)
            ? content.blue_result.all_mappings
            : [];
        const redAllMappings = Array.isArray(content?.red_result?.all_mappings)
            ? content.red_result.all_mappings
            : [];

        if (redAllMappings.length > 0) {
            // Keep history/SSE normalization aligned with the page: a red
            // group without a matching blue feature is not independently renderable.
            const allMappings = redAllMappings
                .map((mappingGroup: any) => normalizeRedAnalysisMappingGroup(mappingGroup, blueAllMappings))
                .filter(Boolean);
            console.debug('[EssentialityClaimInternalReview] normalize red all_mappings:', {
                blueAllMappingsCount: blueAllMappings.length,
                redAllMappingsCount: redAllMappings.length,
                allMappingsCount: allMappings.length,
                reviewActions: redAllMappings.map((mappingGroup: any) => mappingGroup.review_action),
                mappingLabels: redAllMappings.map((mappingGroup: any) => mappingGroup.mapping_label),
                evidenceSources: redAllMappings.map((mappingGroup: any) => {
                    const blueIndex = Number(mappingGroup?.feature_index) - 1;
                    return {
                        featureIndex: mappingGroup?.feature_index,
                        blueMappingCount: blueAllMappings[blueIndex]?.mappings?.length || 0,
                        ignoredRedMappingCount: mappingGroup?.mappings?.length || 0,
                    };
                }),
            });
            return allMappings;
        }

        const mappingChallenges = Array.isArray(content?.red_result?.mapping_challenges)
            ? content.red_result.mapping_challenges
            : [];

        const allMappings = mappingChallenges
            .map((challenge: any) => {
                const blueIndex = Number(challenge?.feature_index) - 1;
                const mappingGroup = Number.isInteger(blueIndex) ? blueAllMappings[blueIndex] : null;
                if (!mappingGroup) {
                    console.debug('[EssentialityClaimInternalReview] unmatched red feature_index:', {
                        featureIndex: challenge?.feature_index,
                        blueAllMappingsCount: blueAllMappings.length,
                    });
                    return null;
                }

                return normalizeRedAnalysisMappingGroup({
                    ...challenge,
                    reason: challenge.challenge || challenge.reason || '',
                    review_analysis: challenge.review_analysis || challenge.challenge || '',
                }, blueAllMappings);
            })
            .filter(Boolean);

        console.debug('[EssentialityClaimInternalReview] normalize red mapping_challenges:', {
            blueAllMappingsCount: blueAllMappings.length,
            mappingChallengesCount: mappingChallenges.length,
            allMappingsCount: allMappings.length,
            renderableEvidenceCounts: allMappings.map((mappingGroup: any) => (
                mappingGroup.mappings?.[0]?.evidence_contexts?.filter((context: any) => context?.context_text?.trim()).length || 0
            )),
            reviewActions: mappingChallenges.map((challenge: any) => challenge.review_action),
            mergedReviewActions: allMappings.map((mappingGroup: any) => mappingGroup.mappings?.[0]?.review_action),
            claimFeatures: allMappings.map((mappingGroup: any) => mappingGroup.claim_feature?.slice(0, 80)),
        });

        return allMappings;
    }

    private getStageResult(content: any, stage: 'blue' | 'red' | 'judge') {
        if (stage === 'judge') {
            return content?.final_result;
        }
        if (stage === 'red') {
            return content?.blue_result || content?.red_result || content?.final_result;
        }

        return content?.final_result || content?.blue_result;
    }

    private normalizeCompareResults(content: any, stage: 'blue' | 'red' | 'judge') {
        if (stage === 'blue' && Array.isArray(content?.compare_results)) {
            return content.compare_results;
        }

        const result = this.getStageResult(content, stage);
        if (!result) {
            if (Array.isArray(content?.compare_results)) {
                return content.compare_results;
            }

            console.warn('[ClaimChartApi] SSE finished without compare result content:', {
                stage,
                currentStage: content?.current_stage,
                stageStatus: content?.stage_status,
                contentKeys: content ? Object.keys(content) : [],
            });
            return [];
        }

        const allMappings = stage === 'red'
            ? this.normalizeRedAllMappings(content)
            : (result.all_mappings || []);
        const revisionAllMappings = stage === 'red'
            ? this.normalizeRedRevisionAllMappings(content)
            : [];
        const judgeStance = stage === 'judge' ? this.normalizeJudgeStance(result.stance) : null;
        const normalizedAllMappings = stage === 'judge'
            ? allMappings.map((mappingGroup: any) => {
                const groupJudgeStance = this.normalizeJudgeStance(mappingGroup?.stance) || judgeStance;
                return {
                    ...mappingGroup,
                    judge_stance: groupJudgeStance,
                    mappings: (mappingGroup.mappings || []).map((mapping: any) => ({
                        ...mapping,
                        judge_stance: this.normalizeJudgeStance(mapping?.stance) || groupJudgeStance,
                    })),
                };
            })
            : allMappings;
        const firstMapping = this.getFirstMapping(normalizedAllMappings);

        return [{
            __is_stage_compare_result: true,
            std_id: '',
            success: true,
            pid: content?.analysis_context?.pid || content?.patent_id || '',
            ts_code: firstMapping.ts_code || '',
            version: firstMapping.version || '',
            claim: content?.analysis_context?.claim || '',
            is_sep: result.is_sep || result.suggested_sep,
            explanation: result.explanation || result.suggested_sep_reason,
            all_mappings: normalizedAllMappings,
            revision_all_mappings: revisionAllMappings,
            cc_result: {
                is_sep: result.is_sep || result.suggested_sep,
                explanation: result.explanation || result.suggested_sep_reason,
                stance: result.stance,
                all_mappings: normalizedAllMappings,
                revision_all_mappings: revisionAllMappings,
                mappings: normalizedAllMappings,
            },
            raw_cc_result: JSON.stringify(result),
        }];
    }

    private normalizeMultiClaimResults(content: any, stage: 'blue' | 'red' | 'judge') {
        if (!Array.isArray(content?.claim_results)) return [];

        return normalizeMultiClaimResultGroups(
            content,
            claimResult => this.normalizeCompareResults(claimResult, stage)
        );
    }

    private getStageFinishedSourceType(stage: 'blue' | 'red' | 'judge') {
        if (stage === 'red') {
            return 'telecom_aicc_Red';
        }
        if (stage === 'judge') {
            return 'telecom_aicc_Judge';
        }
        return 'telecom_aicc_Blue';
    }

    private getStagePageUrl(stage: 'blue' | 'red' | 'judge') {
        if (stage === 'red') {
            return '/essentiality-claim/flow/internal-review';
        }
        if (stage === 'judge') {
            return '/essentiality-claim/flow/cross-validation';
        }
        return '/essentiality-claim/flow/chart';
    }

    private trackStageFinished(taskId: string, stage: 'blue' | 'red' | 'judge') {
        const trackService = injectService<TrackService>(TrackService);
        trackService.trackAir({
            event_type: 'ss_telecom',
            source_type: this.getStageFinishedSourceType(stage),
            session_id: taskId,
            other_param: 'aicc',
            event_detail_str: buildAiccEventDetail(this.getStagePageUrl(stage), {
                taskId,
            }),
        }).catch(error => {
            console.warn(`[ClaimChartApi] Failed to track ${stage} finished:`, error);
        });
    }

    async getClaimChartStream(request: ClaimChartRequest): Promise<PsHttpSSE<ClaimChartResponse>> {
        const sseClient = injectService<FactoryPsHttpSSE>(FactoryPsHttpSSE);
        const stage = this.getCompareStage(request);
        const resolvedPath = await resolveApiPath(`cc_compare/${stage}_sse`);
        // 与 GuestAwareHttpService 保持一致：本地开发 baseURL 为空走 vite proxy，生产/QA 用 VITE_TELECOM_API
        const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_TELECOM_API || '');
        const url = `${baseURL}${resolvedPath}`;

        return sseClient<ClaimChartResponse>()
            .init({
                url,
                data: {
                    task_id: request.task_id,
                },
                hasFinalMessage: () => true,
            });
    }

    async getClaimChart(request: ClaimChartRequest): Promise<ClaimChartResponse> {
        try {
            const stage = this.getCompareStage(request);
            console.debug(`[ClaimChartApi] Starting ${stage} SSE request for task_id: ${request.task_id}`);

            let finalResult: ClaimChartResponse = {
                status: false,
                error_code: -1,
                error_msg: 'No data received from SSE',
                data: { compare_results: [] },
            };

            const stream = await this.getClaimChartStream(request);

            const result = await new Promise<ClaimChartResponse>((resolve, reject) => {
                let hasResolved = false;

                /**
                 * FINISH 消息本身就是后端业务处理完成的准确信号。
                 *
                 * 部分代理会继续保持 EventStream 连接，导致底层 onFinish 晚于最终消息甚至不触发。
                 * 这里统一做一次性结算，让页面 loading 不再依赖网络连接何时关闭；onFinish 仅作为
                 * 未收到可解析 FINISH 内容时的兼容兜底。
                 */
                const resolveFinalResult = (response: ClaimChartResponse) => {
                    if (hasResolved) {
                        return;
                    }
                    hasResolved = true;
                    if (response.status) {
                        this.trackStageFinished(request.task_id, stage);
                    }
                    resolve(response);
                };

                stream
                    .onOpen(() => {
                        console.debug('[ClaimChartApi] SSE connection opened');
                    })
                    .onMessage((event) => {
                        // event: SSEResponse<ClaimChartResponse>
                        if (event.data.status === 'FINISH') {
                            const claimResults = this.normalizeMultiClaimResults(event.data.content, stage);
                            const compareResults = claimResults.length > 0
                                ? flattenMultiClaimResultGroups(claimResults)
                                : this.normalizeCompareResults(event.data.content, stage);
                            console.debug('[ClaimChartApi] SSE final result normalized:', {
                                stage,
                                currentStage: event.data.content?.current_stage,
                                claimCount: claimResults.length,
                                compareResultsCount: compareResults.length,
                            });
                            finalResult = {
                                status: true,
                                error_code: 0,
                                error_msg: '',
                                data: {
                                    compare_results: compareResults,
                                    ...(claimResults.length > 0 ? { claim_results: claimResults } : {}),
                                },
                            };
                            resolveFinalResult(finalResult);
                        }
                    })
                    .onFinish(() => {
                        console.debug('[ClaimChartApi] SSE finished');
                        resolveFinalResult(finalResult);
                    })
                    .onError((err) => {
                        console.error('[ClaimChartApi] SSE error:', err);
                        reject(err);
                    })
                    .start()
                    .catch((err) => {
                        console.error('[ClaimChartApi] SSE start failed:', err);
                        reject(err);
                    });
            });

            console.debug('[ClaimChartApi] SSE request completed');
            return result;
        } catch (error) {
            const formattedError = formatTimeoutError(error, 'Claim Chart API');
            console.error('[ClaimChartApi] Request failed:', formattedError);
            throw new Error(formattedError);
        }
    }
}
